import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { Divider, IconButton, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem, Tooltip } from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useStyles } from './styles';
import { IAuth, IStore } from '../../store/types';
import { Auth, Theme } from '../../store/slices';

interface IHeaderProps {
    onMenuToggle?: () => void;
    mobileMenuOpen?: boolean;
}

/** "jane.doe" → "JD", "Oleh Ivashko" → "OI", "x" → "X". */
export const initialsOf = (name: string): string => {
    const parts = (name || '').trim().split(/[\s._@-]+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
};

const ROLE_LABEL: Record<string, string> = { moderator: 'Moderator', user: 'User' };

/** Top bar: brand on the left, theme switch and the account menu on the right; dark in both themes. */
const Header = ({ onMenuToggle, mobileMenuOpen }: IHeaderProps) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { auth, themeMode } = useSelector<IStore, { auth: IAuth; themeMode: 'light' | 'dark' }>((state) => ({
        auth: state.auth.data,
        themeMode: state.theme.mode,
    }));
    const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);

    const toggleTheme = () => dispatch(Theme.toggleTheme());
    const logOut = () => {
        setMenuAnchor(null);
        dispatch<any>(Auth.logOutUser());
    };
    const nextTheme = themeMode === 'dark' ? 'light' : 'dark';

    return (
        <header className={classes.root}>
            <div className={classes.left}>
                {onMenuToggle && (
                    <IconButton
                        className={classes.menuButton}
                        onClick={onMenuToggle}
                        aria-label={mobileMenuOpen ? 'Close navigation' : 'Open navigation'}
                        aria-expanded={mobileMenuOpen}
                        size="small"
                    >
                        {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                    </IconButton>
                )}
                <Link to="/" className={classes.brand} aria-label="All About Vibe admin panel, home">
                    <img alt="" src="/images/newLogo1.png" className={classes.logo} width={32} height={32} />
                    <span className={classes.wordmark}>All About Vibe</span>
                    <span className={classes.product}>Admin Panel</span>
                </Link>
            </div>
            <div className={classes.right}>
                <Tooltip title={`Switch to ${nextTheme} theme`}>
                    <IconButton className={classes.iconButton} onClick={toggleTheme} aria-label={`Switch to ${nextTheme} theme`} size="small">
                        {themeMode === 'dark' ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
                <button
                    type="button"
                    className={classes.account}
                    onClick={(event) => setMenuAnchor(event.currentTarget)}
                    aria-haspopup="menu"
                    aria-expanded={Boolean(menuAnchor)}
                    aria-controls={menuAnchor ? 'account-menu' : undefined}
                >
                    <span className={classes.avatar} aria-hidden="true">{initialsOf(auth.name)}</span>
                    <span className={classes.accountText}>
                        <span className={classes.userName}>{auth.name}</span>
                        <span className={classes.userRole}>{ROLE_LABEL[auth.role] ?? auth.role}</span>
                    </span>
                    <ExpandMoreIcon className={classes.chevron} fontSize="small" aria-hidden="true" />
                </button>
                <Menu
                    id="account-menu"
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{ sx: { minWidth: 220 } }}
                >
                    <ListSubheader component="div" disableSticky className={classes.menuHeader}>
                        <span className={classes.menuName}>{auth.name}</span>
                        <span className={classes.menuRole}>{ROLE_LABEL[auth.role] ?? auth.role}</span>
                    </ListSubheader>
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem onClick={() => { setMenuAnchor(null); toggleTheme(); }}>
                        <ListItemIcon>
                            {themeMode === 'dark' ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText>{themeMode === 'dark' ? 'Light theme' : 'Dark theme'}</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { setMenuAnchor(null); navigate('/'); }}>
                        <ListItemIcon><HomeOutlinedIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Home</ListItemText>
                    </MenuItem>
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem onClick={logOut}>
                        <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Log out</ListItemText>
                    </MenuItem>
                </Menu>
            </div>
        </header>
    );
};

export default Header;
